import type { AnchorHTMLAttributes, ReactNode, Ref } from 'react'
import type { AppLinkProps } from '@/components/elementary/AppLink/types'
import { buttonVariants, OAuthSplitLayout } from '@/components/ui/button'
import { openCookiePreferencesModal } from '@/lib/analytics/cookie-consent'
import { extractTextFromChildren } from '@/lib/general-helpers'
import { cn } from '@/lib/styles'

const SOCIAL_LOGIN_URL = 'https://app.lokalise.com/login/social' as const

export const getAnchorHref = (href: AppLinkProps['href'], isExternal: boolean, queryString = '') =>
	isExternal ? `${new URL(href as string)}${queryString}` : `${href}${queryString}`

export const getLinkExceptions = (
	options: {
		href: AppLinkProps['href']
		ref: Ref<HTMLAnchorElement>
		linkClassName: string
		props: AnchorHTMLAttributes<HTMLAnchorElement>
	},
	renderContent: () => ReactNode,
) => {
	const { href, ref, linkClassName, props } = options

	if (typeof href === 'string' && props.download) {
		return (
			<a href={href} ref={ref} download className={linkClassName} {...props}>
				{renderContent()}
			</a>
		)
	}

	if (typeof href === 'string' && href === '#intercom') {
		return (
			<a
				href={href}
				ref={ref}
				onClick={(e) => {
					e.preventDefault()
					if (window?.Intercom) {
						window.Intercom('show')
					} else {
						console.warn('Intercom is not initialized')
					}
				}}
				className={linkClassName}
				{...props}
			>
				{renderContent()}
			</a>
		)
	}

	if (typeof href === 'string' && href === '#cookie-settings') {
		return (
			<a
				href={href}
				ref={ref}
				onClick={(e) => {
					e.preventDefault()
					openCookiePreferencesModal()
				}}
				className={linkClassName}
				{...props}
			>
				{renderContent()}
			</a>
		)
	}

	return undefined
}

export const renderContent = (
	props: Pick<AppLinkProps, 'variant' | 'iconStart' | 'iconEnd' | 'label' | 'children'>,
) => (
	<>
		{props.variant === 'google' ? (
			<OAuthSplitLayout>{props.children || props.label}</OAuthSplitLayout>
		) : (
			<>
				{props.iconStart && (
					<div className="flex items-center gap-1">
						<span className="mr-0 group-hover/app-link:mr-1 group-hover/image-link:mr-1 transition-[margin] duration-200">
							{props.iconStart}
						</span>
					</div>
				)}
				{props.children}
				{(props.label || props.iconEnd) && (
					<div className="flex items-center gap-1">
						{props.label && <div className="flex justify-between">{props.label}</div>}
						{props.iconEnd && (
							<span className="transition-transform duration-200 group-hover/app-link:translate-x-1 group-hover/image-link:translate-x-1">
								{props.iconEnd}
							</span>
						)}
					</div>
				)}
			</>
		)}
	</>
)

const isSignUpWithGoogleLink = (href: AppLinkProps['href'], label: AppLinkProps['label']) => {
	return href.toString().startsWith(SOCIAL_LOGIN_URL) && label?.toLowerCase().includes('google')
}

export const determineLinkVariant = (
	href: AppLinkProps['href'],
	label: AppLinkProps['label'],
	variant: AppLinkProps['variant'],
) => (isSignUpWithGoogleLink(href, label) ? 'google' : variant || 'primaryLink')

export function buildLinkClassName({
	variant,
	size,
	inverted,
	className,
	underline,
}: {
	variant: AppLinkProps['variant']
	size: AppLinkProps['size']
	inverted: AppLinkProps['inverted']
	className: string | undefined
	underline: boolean
}) {
	const isOAuth = variant === 'google'
	return cn(
		'group/app-link flex relative flex-row items-center gap-1 h-fit',
		underline && 'group-hover/image-link:border-b-brand-black hover:border-b-brand-black',
		isOAuth ? 'group/oauth' : 'border-b border-transparent',
		cn(buttonVariants({ variant, size, inverted }), className),
	)
}

/**
 * Reads linkUrl and linkText from the live DOM anchor element so that any
 * DOM mutations are reflected in tracked values.
 * Falls back to the original React props if the DOM values are empty.
 */
export const getLiveTrackingProps = (
	anchorEl: HTMLAnchorElement,
	fallback: { href: AppLinkProps['href']; label?: string; children?: ReactNode },
) => ({
	linkUrl: anchorEl.href || String(fallback.href),
	linkText:
		anchorEl.textContent?.trim() || fallback.label || extractTextFromChildren(fallback.children),
})
